<!-- src/pages/LandingPageUAE.vue -->
<template>
  <div class="min-h-screen bg-[#0a0d0f] text-white overflow-x-hidden">
    <!-- ===== HERO ===== -->
<section
  class="relative"
  :style="{
    backgroundImage: `linear-gradient(rgba(10,13,15,.78), rgba(10,13,15,.88)), url(${heroImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }"
>
  <!-- top bar -->
  <div class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
    <div class="flex items-center gap-3">
      <img 
        :src="vwLogo"
        alt="Vehitra World"
        class="h-12 w-auto object-contain"
      />
    </div>
    <div class="text-[11px] opacity-80">
      24/7 Support · WhatsApp: +973 3214 1440
    </div>
  </div>

  <div
    class="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 md:grid-cols-2 md:px-6 md:py-14"
  >
    <!-- LEFT SIDE CONTENT (FIXED TO MATCH YOUR DESIGN) -->
    <div class="flex flex-col justify-center">

      <!-- Eyebrow -->
      <span
        class="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-[12px] font-semibold text-emerald-300 ring-1 ring-white/15"
      >
        ● UAE Chauffeur & Airport Transfer
      </span>

      <!-- Main Heading -->
      <h1 class="leading-tight font-extrabold text-white">
        <span class="block text-4xl md:text-5xl">
          Premium Global
        </span>
        <span class="block text-4xl md:text-5xl">
          Chauffeur & Airport Transfers
        </span>
        <span class="block text-4xl md:text-5xl text-emerald-300">
          for UAE Travelers
        </span>
      </h1>

      <!-- Subtext -->
      <p class="mt-6 max-w-xl text-sm md:text-base text-white/80">
        Serving travellers from Dubai, Abu Dhabi and Sharjah.
      </p>

      <p class="mt-3 max-w-xl text-sm md:text-base text-white/80">
        We provide reliable airport pickups and chauffeur services for your clients  
        and guests. Professional drivers, clean vehicles, flight monitoring and clear  
        pricing for every trip.
      </p>

      <!-- 4 Features -->
      <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-white/80 text-sm">
        <div class="flex items-center gap-2">
          <span class="text-emerald-400 text-lg">✓</span> 24/7 booking & support
        </div>
        <div class="flex items-center gap-2">
          <span class="text-emerald-400 text-lg">✓</span> Meet & greet at airports
        </div>
        <div class="flex items-center gap-2">
          <span class="text-emerald-400 text-lg">✓</span> Flight delay monitoring
        </div>
        <div class="flex items-center gap-2">
          <span class="text-emerald-400 text-lg">✓</span> Confirmed fixed pricing
        </div>
      </div>

      <!-- Buttons -->
      <div class="mt-8 flex flex-wrap gap-3">
        <button
          @click="scrollToForm"
          class="inline-flex items-center justify-center rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-[#050708] transition hover:bg-emerald-400"
        >
          Get Instant Quote
        </button>

   <a
  :href="whatsappUrl"
  target="_blank"
  class="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white/90 hover:bg-white/5"
>
  WhatsApp Our Team
</a>
      </div>

    </div>

    <!-- RIGHT SIDE — YOUR ORIGINAL FORM (UNCHANGED) -->
    <div class="lead-card-wrap">
      <div class="lead-card">
        <h2 class="text-[20px] font-semibold">Get a Quote from UAE</h2>
        <p class="mt-1 text-[12px] text-white/70">
          Share your trip details and we’ll call or WhatsApp you to confirm.
        </p>

        <form
          @submit.prevent="submitLead"
          class="lead-form mt-5 grid grid-cols-1 gap-6 md:grid-cols-2"
        >
              <!-- Name / Phone -->
              <TextField
                label="Full Name*"
                placeholder="e.g. John Doe"
                v-model="form.name"
              />
              <TextField
                label="Mobile / WhatsApp*"
                placeholder="+971 5X XXX XXXX"
                v-model="form.phone"
              />

              <!-- Email full width -->
              <div class="md:col-span-2">
                <TextField
                  label="Email"
                  placeholder="name@company.com"
                  v-model="form.email"
                />
              </div>

              <!-- Pickup + Drop -->
              <div class="md:col-span-2 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="pickUp"
                    type="pickUp"
                    label="Pickup from (City in UAE)*"
                    @addressSelected="addr => onAddressSelected(addr, 'pickUp')"
                  />
                </div>
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="dropOff"
                    type="dropOff"
                    label="Destination (Country & City)*"
                    @addressSelected="addr => onAddressSelected(addr, 'dropOff')"
                  />
                </div>
              </div>

              <!-- Date / Time -->
              <div class="md:col-span-2 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="field-reset">
                  <DateField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.date"
                    @dateChanged="onDateChanged"
                  />
                </div>
                <div class="field-reset">
                  <TimeField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.time"
                    @timeChanged="onTimeChanged"
                  />
                </div>
              </div>

              <!-- Car / Pax / Bags -->
              <SelectField
                class="md:col-span-2 md:max-w-none"
                label="Preferred car type"
                v-model="form.carType"
                :options="carTypeOptions"
                placeholder="Choose car type (optional)"
              />
              <NumberField
                label="Passengers"
                v-model.number="form.passengers"
                min="0"
              />
              <NumberField
                label="Suitcases"
                v-model.number="form.suitcases"
                min="0"
              />

              <!-- Notes -->
              <div class="md:col-span-2">
                <TextArea
                  label="Additional notes (optional)"
                  placeholder="Flight number, child seats, special requests..."
                  v-model="form.notes"
                />
              </div>

              <!-- CTA -->
              <div class="md:col-span-2">
                <button type="submit" :disabled="submitting" class="btn-primary">
                  {{ submitting ? 'Submitting…' : 'Get Confirmed Quote' }}
                </button>
                <p class="mt-2 text-center text-[11px] text-white/60">
                  By submitting, you agree to be contacted about your booking
                  request.
                </p>
              </div>
            </form>
          </div>
        </div>
        <!-- ===== /Right ===== -->
      </div>
    </section>

    <!-- ===== VEHICLES ===== -->
       <!-- ===== CHOOSE YOUR VEHICLE ===== -->
    <section class="bg-[#050911] px-6 py-14">
      <div class="mx-auto max-w-6xl">
        <div class="text-center">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Choose Your Vehicle
          </h2>
          <p class="mt-3 text-sm md:text-base text-white/70">
            Select the ideal vehicle class for your clients and guests.
          </p>
        </div>

        <div
          class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          <article
            v-for="car in vehicleCards"
            :key="car.name"
            class="overflow-hidden rounded-3xl bg-[#070c14] border border-white/8 shadow-[0_24px_60px_rgba(0,0,0,.6)]"
          >
            <!-- Image -->
            <div class="relative h-56 md:h-60 overflow-hidden">
              <img
                :src="car.image"
                :alt="car.name"
                class="h-full w-full object-cover"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
              />
              <h3
                class="absolute bottom-4 left-5 text-xl md:text-2xl font-semibold text-white drop-shadow-lg"
              >
                {{ car.name }}
              </h3>
            </div>

            <!-- Content -->
            <div class="px-6 pb-6 pt-5">
              <p class="text-sm text-white/70 leading-relaxed">
                {{ car.description }}
              </p>

              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  v-for="tag in car.tags"
                  :key="tag"
                  class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/70"
                >
                  {{ tag }}
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>


   <!-- ===== POPULAR TRIPS ===== -->
<section class="bg-[#050911] px-6 pb-16">
  <div class="mx-auto max-w-6xl">
    
    <!-- Section heading -->
    <div class="text-left">
      <h2 class="text-3xl md:text-4xl font-extrabold text-white">
        Popular International Trips from UAE
      </h2>
      <p class="mt-3 text-sm md:text-base text-white/70">
        Our team regularly supports these routes from Dubai, Abu Dhabi and Sharjah.
      </p>
    </div>

    <!-- Cards grid -->
    <div class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">

      <!-- Loop card -->
      <article
        v-for="route in routeCards"
        :key="route.name"
        class="overflow-hidden rounded-3xl bg-[#070c14] border border-white/8 shadow-[0_24px_60px_rgba(0,0,0,.6)]"
      >
        <div class="relative h-64 overflow-hidden">

          <!-- IMAGE -->
          <img :src="route.image" :alt="route.name" class="h-full w-full object-cover" />

          <!-- GRADIENT -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

          <!-- ===== FIXED: TAG PILLS ABOVE THE TITLE ===== -->
          <div class="absolute left-6 bottom-20 flex items-center gap-2 z-20">
            
            <!-- FIRST PILL -->
            <span
              class="rounded-xl border border-emerald-400/40 bg-emerald-500/15
                     px-3 py-1 text-[11px] font-semibold text-emerald-300
                     backdrop-blur-sm shadow-md"
            >
              {{ route.tags[0] }}
            </span>

            <!-- ARROW IF 2 TAGS -->
            <span
              v-if="route.tags.length === 2"
              class="text-emerald-300 text-xs opacity-80"
            >
              →
            </span>

            <!-- SECOND PILL -->
            <span
              v-if="route.tags.length === 2"
              class="rounded-xl border border-emerald-400/40 bg-emerald-500/15
                     px-3 py-1 text-[11px] font-semibold text-emerald-300
                     backdrop-blur-sm shadow-md"
            >
              {{ route.tags[1] }}
            </span>
          </div>

          <!-- TITLE -->
          <h3 class="absolute left-6 bottom-6 text-2xl font-semibold text-white drop-shadow-xl z-20">
            {{ route.name }}
          </h3>

        </div>
      </article>

    </div>
  </div>
</section>

    <!-- ===== WHY CHOOSE US ===== -->
       <!-- ===== WHY CORPORATES CHOOSE US (NEW LAYOUT) ===== -->
    <section class="bg-[#050911] px-6 py-16">
      <div class="mx-auto max-w-6xl">
        <!-- Heading + subtitle -->
        <div class="text-center mb-10">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Why Corporates Choose Us
          </h2>
          <p class="mt-3 text-sm md:text-base text-white/70">
            Trusted by leading travel agencies and multinational companies in the UAE.
          </p>
        </div>

        <!-- 3 × 2 cards -->
        <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div
            v-for="item in whyCards"
            :key="item.title"
            class="rounded-3xl bg-[#070c14] border border-white/8  px-7 py-7 flex flex-col"
          >
            <!-- Icon circle -->
            <div
              class="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-xl text-emerald-400"
            >
              {{ item.icon }}
            </div>

            <!-- Title -->
            <h3 class="text-base md:text-lg font-semibold text-white mb-2">
              {{ item.title }}
            </h3>

            <!-- Copy -->
            <p class="text-sm text-white/70 leading-relaxed">
              {{ item.copy }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TESTIMONIALS ===== -->
   <section class="bg-[#0e1114] px-6 pb-10 pt-14">
  <div class="mx-auto max-w-6xl">

    <!-- Section Header -->
    <div class="mb-10 text-center">
      <h2 class="text-3xl md:text-4xl font-extrabold text-white">
        What Our Clients Say
      </h2>
      <p class="mt-3 text-sm md:text-base text-white/70">
        Trusted by leading travel agencies and multinational companies across the UAE.
      </p>
    </div>

    <!-- Testimonials Grid -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6 relative">
        <p class="text-white/85 text-sm leading-relaxed">
          “Very smooth coordination for our clients travelling from Dubai to Riyadh and Jeddah. Reliable cars and drivers.”
        </p>
        <div class="mt-5 font-semibold text-white">Travel Agency</div>
        <div class="text-xs text-white/50">Dubai</div>
      </div>

      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <p class="text-white/85 text-sm leading-relaxed">
          “We use them for executive trips to Europe and the USA. Our team feels safe and taken care of.”
        </p>
        <div class="mt-5 font-semibold text-white">Corporate Travel Manager</div>
        <div class="text-xs text-white/50">Abu Dhabi</div>
      </div>

      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <p class="text-white/85 text-sm leading-relaxed">
          “Good communication on WhatsApp, especially when flights are delayed.”
        </p>
        <div class="mt-5 font-semibold text-white">Group Travel Planner</div>
        <div class="text-xs text-white/50">Sharjah</div>
      </div>
    </div>
  </div>
</section>

    <!-- ===== FAQ ===== -->
   <!-- ===== FAQ ===== -->
<section class="bg-[#0b0f11] px-6 py-12">
  <div class="mx-auto max-w-5xl">

    <!-- Heading -->
    <h2 class="text-center text-3xl md:text-4xl font-extrabold text-white">
      Frequently Asked Questions
    </h2>

    <div class="mt-10 space-y-4">

      <!-- FAQ ITEM -->
      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <button 
          class="w-full flex justify-between items-center text-left"
          @click="faq1 = !faq1"
        >
          <span class="text-emerald-400 font-semibold text-[15px]">
            Do you support all Emirates?
          </span>
          <span class="text-emerald-400 transition" :class="faq1 ? 'rotate-180' : ''">▲</span>
        </button>
        <div 
          v-if="faq1"
          class="mt-3 text-white/70 text-sm leading-relaxed"
        >
          Yes, we mainly work with clients in Dubai, Abu Dhabi and Sharjah, and can 
          support other Emirates (Ras Al Khaimah, Fujairah) on request.
        </div>
      </div>

      <!-- FAQ ITEM -->
      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <button 
          class="w-full flex justify-between items-center text-left"
          @click="faq2 = !faq2"
        >
          <span class="text-emerald-400 font-semibold text-[15px]">
            Can you handle big groups for events in Saudi?
          </span>
          <span class="text-emerald-400 transition" :class="faq2 ? 'rotate-180' : ''">▲</span>
        </button>
        <div 
          v-if="faq2"
          class="mt-3 text-white/70 text-sm leading-relaxed"
        >
          Yes. We can arrange multiple vehicles or premium minibuses for conferences, exhibitions, 
          and delegations ensuring everyone arrives together.
        </div>
      </div>

      <!-- FAQ ITEM -->
      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <button 
          class="w-full flex justify-between items-center text-left"
          @click="faq3 = !faq3"
        >
          <span class="text-emerald-400 font-semibold text-[15px]">
            How do we get the final price?
          </span>
          <span class="text-emerald-400 transition" :class="faq3 ? 'rotate-180' : ''">▲</span>
        </button>
        <div 
          v-if="faq3"
          class="mt-3 text-white/70 text-sm leading-relaxed"
        >
          After you submit the form, our team will calculate the best option based on your 
          route and vehicle choice, then send you a clear quote by email or WhatsApp.
        </div>
      </div>

      <!-- FAQ ITEM -->
      <div class="rounded-3xl border border-white/10 bg-[#0f1317] p-6">
        <button 
          class="w-full flex justify-between items-center text-left"
          @click="faq4 = !faq4"
        >
          <span class="text-emerald-400 font-semibold text-[15px]">
            Can travel agencies and corporates have long-term agreements?
          </span>
          <span class="text-emerald-400 transition" :class="faq4 ? 'rotate-180' : ''">▲</span>
        </button>
        <div 
          v-if="faq4"
          class="mt-3 text-white/70 text-sm leading-relaxed"
        >
          Yes, we can discuss regular cooperation, monthly billing, and tailored pricing 
          for frequent bookings originating from the UAE.
        </div>
      </div>

    </div>
  </div>
</section>

    <!-- ===== CTA ===== -->
    <section class="bg-[#01060d] px-6 py-20 relative overflow-hidden">
  <!-- Soft radial glow background -->
  <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
    <div class="h-[600px] w-[600px] rounded-full bg-emerald-600/10 blur-[160px]"></div>
  </div>

  <div class="mx-auto max-w-4xl text-center relative z-10">
    <h2 class="text-3xl md:text-4xl font-extrabold text-white leading-tight">
      Ready to plan your next trip from <span class="text-emerald-400">UAE?</span>
    </h2>

    <p class="mx-auto mt-4 max-w-2xl text-white/70 text-sm md:text-base leading-relaxed">
      Tell us your route from Dubai, Abu Dhabi or Sharjah, and we’ll take care of
      your ground transportation in Saudi, GCC, Europe or the USA.
    </p>

    <button
      @click="scrollToForm"
      class="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 text-base font-semibold text-[#020617] shadow-lg shadow-emerald-500/30 hover:bg-emerald-400 transition"
    >
      Get Instant Quote
      <span class="text-xl">↗</span>
    </button>
  </div>
</section>

<!-- ===== FOOTER ===== -->
<footer class="bg-[#000409] px-6 py-8 border-t border-white/10">
  <div class="mx-auto max-w-6xl text-center text-[12px] text-white/40">
    © 2025 Vehitra World • All Rights Reserved  
    <br />
    <span class="text-white/30">Privacy Policy · Terms · Contact</span>
  </div>
</footer>

  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  ref,
  defineComponent,
  h,
  onMounted,
  onBeforeUnmount,
  computed,  
} from 'vue'
import { useRouter } from 'vue-router'
import LocationField from '@/components/search/LocationField.vue'
import DateField from '@/components/search/DateField.vue'
import TimeField from '@/components/search/TimeField.vue'
import { getBackendBaseUrl } from '@/utils/common'

import heroImageUrl from '@/assets/landing/airport-hero.jpg'
const heroImage = heroImageUrl

import vwLogo from '@/assets/logo.png'

// Vehicle & route images
import sClassImg from '@/assets/landing/uae/s-class.jpg'
import vClassImg from '@/assets/landing/uae/v-class.jpg'
import eClassImg from '@/assets/landing/uae/e-class.jpg'

import saudiImg from '@/assets/landing/uae/saudi.jpg'
import qatarImg from '@/assets/landing/uae/qatar-gcc.jpg'
import europeImg from '@/assets/landing/uae/europe-usa.jpg'



const router = useRouter()
const submitting = ref(false)
const carTypeOptions = ['Premium', 'Luxury', 'SUV', 'Van']

const faq1 = ref(false)
const faq2 = ref(false)
const faq3 = ref(false)
const faq4 = ref(false)

const form = reactive({
  name: '',
  phone: '',
  email: '',
  pickup: '',
  dropoff: '',
  datetime: '',
  notes: '',
  carType: '',
  passengers: 0,
  suitcases: 0,
})

// Whats app
const WHATSAPP_NUMBER = '97332141440' // no +
const whatsappMessage =
  'Hi, I saw your website in google. I would like an airport transfer quote from UAE (Dubai/Abu Dhabi/Sharjah).'

const whatsappUrl = computed(
  () =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`
)

// Data for cards
const vehicleCards = [
  {
    name: 'Mercedes S-Class',
    image: sClassImg,
    description:
      'Luxury sedan for senior executives, VIP guests and special occasions. Unmatched comfort and prestige.',
    tags: ['3 PASSENGERS', '2 LUGGAGE', 'FIRST CLASS', 'WI-FI'],
  },
  {
    name: 'Mercedes V-Class',
    image: vClassImg,
    description:
      'Spacious van for families, groups and corporate teams with extra luggage. Business conference style seating.',
    tags: ['5 PASSENGERS', '5 LUGGAGE', 'CONFERENCE LAYOUT', 'SPACIOUS'],
  },
  {
    name: 'Mercedes E-Class',
    image: eClassImg,
    description:
      'Comfortable executive sedan for regular airport runs and business trips. The standard for corporate travel.',
    tags: ['3 PASSENGERS', '2 LUGGAGE', 'BUSINESS CLASS', 'LEATHER'],
  },
]

const routeCards = [
  {
    name: 'Saudi Arabia',
    image: saudiImg,
    tags: ['UAE', 'SAUDI ARABIA'],
  },
  {
    name: 'Qatar & GCC',
    image: qatarImg,
    tags: ['UAE', 'QATAR & GCC'],
  },
  {
    name: 'Europe & USA',
    image: europeImg,
    tags: ['UAE', 'EUROPE & USA'],
  },
]


const whyCards = [
  {
    title: 'UAE-Focused',
    copy:
      'Built specifically for travellers starting from Dubai, Abu Dhabi and Sharjah.',
    icon: '📍',
  },
  {
    title: 'Global Coverage',
    copy:
      'One point of contact for trips to Saudi, Qatar, Bahrain, Kuwait, Oman, Europe and USA.',
    icon: '🌍',
  },
  {
    title: 'Professional Chauffeurs',
    copy:
      'Trained, polite drivers with premium vehicles ensuring a smooth ride.',
    icon: '👤',
  },
  {
    title: 'Meet & Greet',
    copy:
      'Drivers welcome guests at arrivals with clear signage and luggage assistance.',
    icon: '🎉',
  },
  {
    title: 'Flight Monitoring',
    copy:
      'We track flight status and adjust pickup times automatically when needed.',
    icon: '✈️',
  },
  {
    title: '24/7 Support',
    copy:
      'Round-the-clock customer service team ready to assist you anytime.',
    icon: '🎧',
  },
]

const pickUp = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
  date: null as string | null,
  time: '' as string,
})
const dropOff = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
})

const onAddressSelected = (address: any, type: 'pickUp' | 'dropOff') => {
  const add = address ?? {
    id: '',
    name: '',
    description: '',
    coordinates: { lat: null, lng: null },
  }
  if (type === 'pickUp') {
    pickUp.location = add
    form.pickup = add?.name || add?.description || ''
  } else {
    dropOff.location = add
    form.dropoff = add?.name || add?.description || ''
  }
}
const onDateChanged = (d: string) => {
  pickUp.date = d
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}
const onTimeChanged = (t: string) => {
  pickUp.time = t
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}

const scrollToForm = () => {
  const els = document.querySelectorAll('form')
  if (els && els[0])
    els[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const submitLead = async () => {
  if (!form.name || !form.phone || !form.pickup || !form.dropoff || !form.datetime) {
    alert('Please fill all required fields.')
    return
  }
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
    alert('Please enter a valid email.')
    return
  }
  if (form.passengers < 0 || form.suitcases < 0) {
    alert('Passengers / Suitcases cannot be negative.')
    return
  }

  const API_BASE = getBackendBaseUrl()
  const payload = {
    name: form.name,
    phone: form.phone,
    email: form.email || null,
    pickup: form.pickup,
    dropoff: form.dropoff,
    datetime: form.datetime,
    notes: form.notes || null,
    source: 'uae-airport-transfer',
    car_type: form.carType || null,
    passengers: Number(form.passengers) || 0,
    suitcases: Number(form.suitcases) || 0,
    pickUp,
    dropOff,
  }

  submitting.value = true
  try {
    const res = await fetch(`${API_BASE}api/leads/airport-transfer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(`Request failed (${res.status}) ${msg}`)
    }
    router.push({ name: 'ThankYou' })
  } catch (e: any) {
    alert(e?.message || 'Something went wrong while submitting your request.')
  } finally {
    submitting.value = false
  }
}

/* ---------- Google Places dropdown patch (same as old page) ---------- */
function usePatchPacDropdown() {
  let rafId: number | null = null
  let running = false
  const getFocusedInput = (): HTMLInputElement | null =>
    document.activeElement?.tagName === 'INPUT'
      ? (document.activeElement as HTMLInputElement)
      : (document.querySelector('.lead-form input:focus') as HTMLInputElement | null)

  const apply = () => {
    const pacs = document.querySelectorAll<HTMLElement>('.pac-container')
    if (!pacs.length) return
    const input =
      getFocusedInput() ||
      (document.querySelector('.lead-form input') as HTMLInputElement | null)
    if (!input) return
    const r = input.getBoundingClientRect()
    const desiredWidth = Math.max(r.width + 40, 560)
    pacs.forEach(pac => {
      pac.style.setProperty('position', 'fixed', 'important')
      pac.style.setProperty('left', `${r.left}px`, 'important')
      pac.style.setProperty('top', `${r.bottom}px`, 'important')
      pac.style.setProperty('width', `${desiredWidth}px`, 'important')
      pac.style.setProperty('minWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxHeight', '480px', 'important')
      pac.style.setProperty('overflowY', 'auto', 'important')
      pac.style.setProperty('overflowX', 'hidden', 'important')
      pac.style.setProperty('zIndex', '99999', 'important')
      pac.style.setProperty('borderRadius', '12px', 'important')
      pac.style.setProperty('border', '1px solid rgba(255,255,255,.18)', 'important')
      pac.style.setProperty('background', 'rgba(15,19,23,.98)', 'important')
      pac.style.setProperty('boxShadow', '0 12px 40px rgba(0,0,0,.5)', 'important')
      pac.style.setProperty('padding', '4px 0', 'important')
    })
  }
  const loop = () => {
    apply()
    if (running) rafId = requestAnimationFrame(loop)
  }
  const start = () => {
    if (!running) {
      running = true
      loop()
    }
  }
  const stop = () => {
    running = false
    if (rafId) cancelAnimationFrame(rafId)
    rafId = null
  }
  const onFocusIn = () => setTimeout(() => { apply(); start() }, 0)
  const onFocusOut = () => setTimeout(stop, 150)
  const onResize = () => apply()
  const onScroll = () => apply()

  onMounted(() => {
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', onFocusOut, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
  })
  onBeforeUnmount(() => {
    document.removeEventListener('focusin', onFocusIn, true)
    document.removeEventListener('focusout', onFocusOut, true)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, true)
    stop()
  })
}
usePatchPacDropdown()

/* ---------- Reusable mini components (same as old page) ---------- */
const Badge = defineComponent({
  name: 'Badge',
  props: { icon: String },
  setup(props, { slots }) {
    const iconMap: Record<string, string> = {
      lifebuoy: '🛟',
      smile: '🙂',
      plane: '✈️',
    }
    return () =>
      h(
        'span',
        {
          class:
            'inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10',
        },
        [h('span', { class: 'opacity-80' }, iconMap[props.icon || ''] || '•'), slots.default?.()],
      )
  },
})

const TextField = defineComponent({
  name: 'TextField',
  props: {
    label: String,
    modelValue: [String, Number],
    placeholder: String,
    type: { type: String, default: 'text' },
    min: [String, Number],
  },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () =>
      h('label', { class: 'block', ...attrs }, [
        h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
        h('input', {
          class:
            'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
          value: props.modelValue,
          placeholder: props.placeholder,
          onInput,
          type: props.type,
          min: props.min,
        }),
      ])
  },
})

const NumberField = defineComponent({
  name: 'NumberField',
  props: {
    label: String,
    modelValue: [String, Number],
    min: { type: [String, Number], default: 0 },
  },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) =>
      emit('update:modelValue', e.target.value === '' ? '' : Number(e.target.value))
    return () =>
      h('label', { class: 'block', ...attrs }, [
        h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
        h('input', {
          class:
            'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
          value: props.modelValue,
          type: 'number',
          min: props.min,
          onInput,
        }),
      ])
  },
})

const SelectField = defineComponent({
  name: 'SelectField',
  props: {
    label: String,
    modelValue: String,
    options: Array as () => string[],
    placeholder: String,
  },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onChange = (e: any) => emit('update:modelValue', e.target.value)
    return () =>
      h('label', { class: 'block', ...attrs }, [
        h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
        h(
          'select',
          {
            class:
              'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none focus:border-white/30',
            value: props.modelValue ?? '',
            onChange,
          },
          [
            h('option', { value: '' }, props.placeholder || 'Select…'),
            ...(props.options || []).map(o => h('option', { value: o }, o)),
          ],
        ),
      ])
  },
})

const TextArea = defineComponent({
  name: 'TextArea',
  props: { label: String, modelValue: String, placeholder: String },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () =>
      h('label', { class: 'block', ...attrs }, [
        h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
        h('textarea', {
          class:
            'min-h-[90px] w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
          value: props.modelValue,
          placeholder: props.placeholder,
          onInput,
        }),
      ])
  },
})

const FeatureCard = defineComponent({
  name: 'FeatureCard',
  props: { title: String, copy: String },
  setup(props) {
    return () =>
      h('div', { class: 'rounded-2xl border border-white/10 bg-[#0f1317] p-5' }, [
        h('div', { class: 'text-[13px] font-semibold' }, props.title),
        h('p', { class: 'mt-2 text-sm text-white/70' }, props.copy),
      ])
  },
})

const Testimonial = defineComponent({
  name: 'Testimonial',
  props: { text: String, who: String },
  setup(props) {
    return () =>
      h('div', { class: 'rounded-2xl border border-white/10 bg-[#0f1317] p-5' }, [
        h('div', { class: 'mb-2 text-emerald-400' }, '★★★★★'),
        h('p', { class: 'text-sm text-white/85' }, `“${props.text}”`),
        h('div', { class: 'mt-3 text-xs text-white/50' }, props.who),
      ])
  },
})

const FAQ = defineComponent({
  name: 'FAQ',
  props: { q: String, a: String },
  setup(props) {
    const open = ref(false)
    const toggle = () => (open.value = !open.value)
    return () =>
      h('div', { class: 'overflow-hidden rounded-2xl border border-white/10 bg-[#0f1317]' }, [
        h(
          'button',
          {
            class:
              'flex w-full items-center justify-between px-4 py-4 text-left text-sm font-semibold',
            onClick: toggle,
          },
          [h('span', props.q), h('span', { class: (open.value ? 'rotate-90 ' : '') + 'transition' }, '▸')],
        ),
        open.value &&
          h('div', { class: 'px-4 pb-4 text-sm text-white/70' }, props.a),
      ])
  },
})
</script>

<style scoped>
:global(:root) {
  --lead-input-h: 56px;
  --lead-border: rgba(255,255,255,.14);
  --lead-fill: rgba(255,255,255,.06);
  --lead-focus: rgba(255,255,255,.32);
}
.lead-card-wrap {
  width: 100%;
}
@screen md {
  .lead-card-wrap {
    @apply max-w-[560px] justify-self-end;
  }
}
.lead-card {
  @apply rounded-2xl backdrop-blur-2xl p-6 md:p-8;
  background: linear-gradient(180deg, rgba(255,255,255,.1), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 20px 60px rgba(0,0,0,.35);
}
.btn-primary {
  @apply w-full rounded-full font-semibold transition px-5 py-4 text-[#0a0d0f] bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60;
  font-size: 16px;
}

/* Location + date/time resets (same idea as older page) */
.lead-form :deep(.af-loc.relative) {
  padding: 0 !important;
  overflow: visible !important;
}
.lead-form :deep(.af-loc .flex.flex-col.space-y-2) {
  gap: 6px !important;
}
.lead-form :deep(.af-loc .text-small),
.lead-form :deep(.af-loc .text-small *),
.lead-form :deep(.af-loc [class*='uppercase']) {
  text-transform: none !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  letter-spacing: 0.02em !important;
  color: rgba(255,255,255,.7) !important;
  font-weight: 600 !important;
}
.lead-form :deep(.af-loc input[type='text']) {
  height: var(--lead-input-h) !important;
  min-height: var(--lead-input-h) !important;
  border-radius: 12px !important;
  border: 1px solid var(--lead-border) !important;
  background: var(--lead-fill) !important;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important;
  box-shadow: none !important;
}
/* Hide the green "X" clear button in the location fields */



/* RESET inner flex wrapper of Date & Time fields */
.lead-form :deep(.af-dt .flex.flex-col.space-y-2) {
  gap: 6px !important;   /* same as LocationField */
  padding: 0 !important;
  margin: 0 !important;
}

.lead-form :deep(.af-dt .flex.flex-col.space-y-2 label) {
  margin-bottom: 6px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: rgba(255,255,255,.7) !important;
}
/* Date / Time wrapper reset */
.lead-form :deep(.af-dt),
.lead-form :deep(.af-dt [class*="p-"]),
.lead-form :deep(.af-dt [class*="px-"]),
.lead-form :deep(.af-dt [class*="py-"]),
.lead-form :deep(.af-dt [class*="m-"]) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Date / Time labels – match other labels */
.lead-form :deep(.af-dt label),
.lead-form :deep(.af-dt .label),
.lead-form :deep(.af-dt [class*="uppercase"]) {
  text-transform: none !important;
  margin-bottom: 6px !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  letter-spacing: 0.02em !important;
  color: rgba(255,255,255,.7) !important;
  font-weight: 600 !important;
}

/* Date / Time inputs – same style as other inputs */
.lead-form :deep(.af-dt input),
.lead-form :deep(.af-dt input[type='text']),
.lead-form :deep(.af-dt input[type='date']),
.lead-form :deep(.af-dt input[type='time']) {
  width: 100% !important;
  height: var(--lead-input-h) !important;
  min-height: var(--lead-input-h) !important;
  border-radius: 12px !important;
  border: 1px solid var(--lead-border) !important;
  background: var(--lead-fill) !important;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important;
  box-shadow: none !important;
  color-scheme: dark;
}

.lead-form :deep(.af-dt input::placeholder) {
  color: rgba(255,255,255,.38) !important;
  font-weight: 500 !important;
}

.lead-form :deep(.af-dt input[type='date']::-webkit-calendar-picker-indicator),
.lead-form :deep(.af-dt input[type='time']::-webkit-calendar-picker-indicator) {
  filter: invert(1) opacity(.85);
}

.lead-form :deep(.af-dt.relative) {
  overflow: visible !important;
}
/* Date / Time dark style */
.lead-form :deep(.af-dt input),
.lead-form :deep(.af-dt input[type='text']),
.lead-form :deep(.af-dt input[type='date']),
.lead-form :deep(.af-dt input[type='time']) {
  width: 100% !important;
  height: var(--lead-input-h) !important;
  min-height: var(--lead-input-h) !important;
  border-radius: 12px !important;
  border: 1px solid var(--lead-border) !important;
  background: var(--lead-fill) !important;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important;
  box-shadow: none !important;
  color-scheme: dark;

  
}

/* Hide the green clear (X) badge inside LocationField without breaking layout */
.lead-form :deep(.af-loc .bg-emerald-500),
.lead-form :deep(.af-loc .bg-emerald-600) {
  position: absolute !important;
  right: -9999px !important;  /* move it far offscreen */
  opacity: 0 !important;
  pointer-events: none !important;
  width: 0 !important;
  height: 0 !important;
}



</style>

<style>
.pac-item {
  color: #d8dee9 !important;
  font-size: 15px !important;
  line-height: 1.4 !important;
  padding: 12px 16px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.pac-item:hover {
  background: rgba(255,255,255,.08) !important;
}
.pac-item-query {
  font-weight: 700 !important;
  color: #fff !important;
}
.pac-icon {
  filter: brightness(0) invert(1) !important;
  margin-right: 8px !important;
}

.route-chip {
  @apply rounded-lg border border-emerald-500/40 bg-white/5 backdrop-blur-sm
         px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300;
}
</style>